// 資源效果處理器
// Resource Effect Handlers - 充能、耗能、獎勵等

import type { GameState } from '../../../types';
import type { ImmediateAction, EffectHandler } from '../EffectTriggerSystem';
import { getLogger } from '../../../utils/logger';

const log = getLogger('ResourceHandlers');

/**
 * 充能效果處理器
 * 蒸汽世界核心機制
 */
export class ChargeHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('充能');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「充能+X」
    const chargeMatch = effectText.match(/充能\+(\d+)/);
    if (chargeMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'charge',
        value: parseInt(chargeMatch[1], 10),
      });
    }

    // 解析「你充能時...」（觸發條件）
    if (effectText.includes('你充能時')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'onCharge',
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'charge') return state;

    const chargeValue = typeof action.value === 'number' ? action.value : 0;
    if (chargeValue === 0) return state;

    const playerId = state.activePlayerId;
    const isPlayer1 = playerId === state.player1.id;
    const playerKey = isPlayer1 ? 'player1' : 'player2';
    const player = state[playerKey];

    // 增加充能值
    const currentEnergy = player.energy ?? 0;
    const newEnergy = currentEnergy + chargeValue;

    log.debug(`充能 +${String(chargeValue)}, now ${String(newEnergy)}`);

    return {
      ...state,
      [playerKey]: {
        ...player,
        energy: newEnergy,
      },
    };
  }
}

/**
 * 耗能效果處理器
 */
export class ConsumeEnergyHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('耗能');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「耗能X」
    const consumeMatch = effectText.match(/耗能(\d+)/);
    if (consumeMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'consumeEnergy',
        value: parseInt(consumeMatch[1], 10),
      });
    }

    // 解析「耗能-X」（減少耗能）
    const reduceMatch = effectText.match(/耗能-(\d+)/);
    if (reduceMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'reduceConsumption',
        value: parseInt(reduceMatch[1], 10),
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'consumeEnergy') return state;

    const consumeValue = typeof action.value === 'number' ? action.value : 0;
    if (consumeValue === 0) return state;

    const playerId = state.activePlayerId;
    const isPlayer1 = playerId === state.player1.id;
    const playerKey = isPlayer1 ? 'player1' : 'player2';
    const player = state[playerKey];

    const currentEnergy = player.energy ?? 0;
    
    // 檢查是否有足夠能量
    if (currentEnergy < consumeValue) {
      log.debug(`Not enough energy: ${String(currentEnergy)} < ${String(consumeValue)}`);
      return state;
    }

    const newEnergy = currentEnergy - consumeValue;
    log.debug(`耗能 ${String(consumeValue)}, now ${String(newEnergy)}`);

    return {
      ...state,
      [playerKey]: {
        ...player,
        energy: newEnergy,
      },
    };
  }
}

/**
 * 獎勵效果處理器（增強版）
 */
export class RewardEffectHandlerV2 implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('獎勵');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「獎勵+X」
    const addMatch = effectText.match(/(?:使(?:你|敵方))?獎勵\+(\d+)/g);
    if (addMatch) {
      for (const match of addMatch) {
        const valueMatch = match.match(/獎勵\+(\d+)/);
        const isEnemy = match.includes('敵方');
        if (valueMatch) {
          actions.push({
            type: 'custom',
            sourceCardId: '',
            customHandler: 'addReward',
            value: parseInt(valueMatch[1], 10),
            targetSelector: {
              zone: 'battlefield',
              side: isEnemy ? 'enemy' : 'self',
            },
          });
        }
      }
    }

    // 解析「獎勵-X」
    const subMatch = effectText.match(/獎勵-(\d+)/);
    if (subMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'subtractReward',
        value: parseInt(subMatch[1], 10),
      });
    }

    // 解析「獎勵+X。X=...」（動態獎勵）
    const dynamicMatch = effectText.match(/獎勵\+X[^X]*X=([^\s]+)/);
    if (dynamicMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'dynamicReward',
        value: dynamicMatch[1], // 計算公式
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    const playerId = state.activePlayerId;
    const isPlayer1 = playerId === state.player1.id;

    if (action.customHandler === 'addReward') {
      const rewardValue = typeof action.value === 'number' ? action.value : 0;
      const isEnemy = action.targetSelector?.side === 'enemy';
      const targetIsPlayer1 = isEnemy ? !isPlayer1 : isPlayer1;
      const playerKey = targetIsPlayer1 ? 'player1' : 'player2';
      const player = state[playerKey];

      const currentReward = player.reward ?? 0;
      const newReward = currentReward + rewardValue;

      log.debug(`${isEnemy ? '敵方' : '你'} 獎勵 +${String(rewardValue)}, now ${String(newReward)}`);

      return {
        ...state,
        [playerKey]: {
          ...player,
          reward: newReward,
        },
      };
    }

    if (action.customHandler === 'subtractReward') {
      const rewardValue = typeof action.value === 'number' ? action.value : 0;
      const playerKey = isPlayer1 ? 'player1' : 'player2';
      const player = state[playerKey];

      const currentReward = player.reward ?? 0;
      const newReward = Math.max(0, currentReward - rewardValue);

      log.debug(`獎勵 -${String(rewardValue)}, now ${String(newReward)}`);

      return {
        ...state,
        [playerKey]: {
          ...player,
          reward: newReward,
        },
      };
    }

    if (action.customHandler === 'dynamicReward') {
      // 動態獎勵需要計算
      log.debug(`Dynamic reward: X=${String(action.value ?? 'unknown')}`);
      // TODO: 實作動態計算
    }

    return state;
  }
}

/**
 * 抽牌效果處理器（增強版）
 */
export class DrawEffectHandlerV2 implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('抽') && text.includes('牌');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「抽X張牌」
    const drawMatch = effectText.match(/抽(\d+)張牌?/);
    if (drawMatch) {
      const isEnemy = effectText.includes('使敵方抽');
      actions.push({
        type: 'draw',
        sourceCardId: '',
        value: parseInt(drawMatch[1], 10),
        targetSelector: isEnemy ? {
          zone: 'deck',
          side: 'enemy',
        } : undefined,
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.type !== 'draw') return state;

    const drawCount = typeof action.value === 'number' ? action.value : 1;
    const isEnemy = action.targetSelector?.side === 'enemy';
    const activeIsPlayer1 = state.activePlayerId === state.player1.id;
    const targetIsPlayer1 = isEnemy ? !activeIsPlayer1 : activeIsPlayer1;
    const playerKey = targetIsPlayer1 ? 'player1' : 'player2';
    const player = state[playerKey];

    const newDeck = [...player.deck];
    const newHand = [...player.hand];
    const cardsToDraw = Math.min(drawCount, newDeck.length);

    for (let i = 0; i < cardsToDraw; i++) {
      const card = newDeck.shift();
      if (card) {
        newHand.push({ ...card, location: 'hand' });
      }
    }

    log.debug(`${isEnemy ? '敵方' : '你'} 抽 ${String(cardsToDraw)} 張牌`);

    return {
      ...state,
      [playerKey]: {
        ...player,
        deck: newDeck,
        hand: newHand,
      },
    };
  }
}

/**
 * 棄牌效果處理器（增強版）
 */
export class DiscardEffectHandlerV2 implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('棄') && (text.includes('手牌') || text.includes('牌'));
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「棄掉X張手牌」
    const discardMatch = effectText.match(/棄掉?(\d*)張?手牌/);
    if (discardMatch) {
      const isSelf = effectText.includes('你棄') || !effectText.includes('敵方');
      actions.push({
        type: 'discard',
        sourceCardId: '',
        value: discardMatch[1] ? parseInt(discardMatch[1], 10) : 1,
        targetSelector: {
          zone: 'hand',
          side: isSelf ? 'self' : 'enemy',
        },
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.type !== 'discard') return state;

    const count = typeof action.value === 'number' ? action.value : 1;
    const isSelf = action.targetSelector?.side !== 'enemy';
    const activeIsPlayer1 = state.activePlayerId === state.player1.id;
    const targetIsPlayer1 = isSelf ? activeIsPlayer1 : !activeIsPlayer1;
    const playerKey = targetIsPlayer1 ? 'player1' : 'player2';
    const player = state[playerKey];

    const hand = [...player.hand];
    const graveyard = [...player.graveyard];

    const toDiscard = Math.min(count, hand.length);
    for (let i = 0; i < toDiscard; i++) {
      const card = hand.pop();
      if (card) {
        graveyard.push({ ...card, location: 'graveyard' as const });
      }
    }

    log.debug(`${isSelf ? '你' : '敵方'} 棄 ${String(toDiscard)} 張手牌`);

    return {
      ...state,
      [playerKey]: {
        ...player,
        hand,
        graveyard,
      },
    };
  }
}

// 匯出所有資源處理器
export const resourceHandlers = {
  ChargeHandler,
  ConsumeEnergyHandler,
  RewardEffectHandlerV2,
  DrawEffectHandlerV2,
  DiscardEffectHandlerV2,
};
